import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { AddressIcon, PhoneIcon, EmailIcon, ClockIcon } from "../assets/svgs/ContactIcons";
import { ReactNode } from "react";
import { FiArrowUpRight } from "react-icons/fi";

interface SocialLink {
  icon: string;
  color: string;
  hover: string;
  url: string;
  label: string;
  iconComponent?: ReactNode;
}

export default function Contact() {
  return (
    <>
     

      <SEO
        title="Contact ZeeXSoft - Get Your Free Quote Today"
        description="Contact ZeeXSoft for your software development needs. Located in Sharjah, UAE. Get a free quote for web development, mobile apps, and digital solutions."
        keywords="contact ZeeXSoft, software development quote UAE, web development Sharjah, free consultation"
      />

      {/* Hero Section */}
      <section className="relative pt-40 pb-28 overflow-hidden bg-gradient-to-b from-background to-muted/5">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/10 to-transparent"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        </div>
        <div className="container relative z-10 px-6 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.span 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest text-primary uppercase rounded-full bg-primary/10"
            >
              Let's Talk
            </motion.span>
            <motion.h1 
              className="mb-6 text-5xl font-bold md:text-6xl lg:text-7xl font-orbitron bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Get In Touch
            </motion.h1>
            <motion.p 
              className="max-w-3xl mx-auto text-xl md:text-2xl text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Ready to start your next project? Let's discuss how we can help architect your digital future.
            </motion.p>
            
            {/* WhatsApp CTA Button */}
            <motion.div
              className="flex justify-center mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {(() => {
                const phoneNumber = "447743307781";
                const message = "Hello! I'm interested in your services and would like to discuss my project.";
                const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                
                return (
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-12 py-5 font-medium rounded-lg bg-[#25D366] text-white hover:shadow-lg transition-all flex items-center justify-center gap-3 text-lg"
                  >
                    <i className="fab fa-whatsapp text-2xl"></i>
                    Message Us on WhatsApp
                  </a>
                );
              })()}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* New Stats Section */}
      <section className="py-16 bg-gradient-to-b from-muted/5 to-background">
        <div className="container px-6 mx-auto">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {[
              { value: '24/7', label: 'Support Availability' },
              { value: '1h', label: 'Avg. Response Time' },
              { value: '100%', label: 'Client Satisfaction' },
              { value: '500+', label: 'Projects Completed' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-card/50 backdrop-blur-sm rounded-xl border border-border/20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-0 w-full h-1/3 bg-gradient-to-b from-background to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-background to-transparent"></div>
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-secondary/5 blur-3xl"></div>
        </div>
        
        <div className="container px-6 mx-auto">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
              className="space-y-8"
            >
              <div className="p-8 rounded-2xl bg-card/80 backdrop-blur-sm border border-border/20 shadow-lg">
                <h3 className="font-orbitron font-bold text-2xl md:text-3xl mb-8 text-foreground">
                  Contact Information
                </h3>
                
                <div className="space-y-6">
                  {[
                    {
                      icon: <AddressIcon className="w-6 h-6" />,
                      title: "Address",
                      content: "Almawarid Building, Industrial Area 3, Sharjah, UAE",
                      action: "View on Map",
                      url: "https://maps.app.goo.gl/DgTfJ51jwL9Ma5wV7",
                      color: "from-blue-500/20 to-indigo-500/20"
                    },
                    {
                      icon: <PhoneIcon className="w-6 h-6" />,
                      title: "Phone",
                      content: "+971-58-248-1443",
                      action: "Call Now",
                      url: "tel:+971582481443",
                      color: "from-green-500/20 to-teal-500/20"
                    },
                    {
                      icon: <EmailIcon className="w-6 h-6" />,
                      title: "Email",
                      content: "info@zeexsoft.com",
                      action: "Send Email",
                      url: "mailto:info@zeexsoft.com",
                      color: "from-purple-500/20 to-pink-500/20"
                    },
                    {
                      icon: <ClockIcon className="w-6 h-6" />,
                      title: "Working Hours",
                      content: "Mon-Fri: 9AM - 6PM",
                      action: "Book Appointment",
                      url: "#contact-form",
                      color: "from-orange-500/20 to-amber-500/20"
                    }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      className="flex items-center space-x-6 p-4 rounded-xl bg-muted/10 hover:bg-muted/20 transition-colors group border border-border/10"
                      whileHover={{ x: 5 }}
                    >
                      <a 
                        href={item.url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${item.color} group-hover:scale-110 transition-transform"
                      >
                        {item.icon}
                      </a>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground mb-1">{item.title}</p>
                        <p className="text-muted-foreground text-sm mb-2">{item.content}</p>
                        <a 
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-primary hover:underline inline-flex items-center"
                        >
                          {item.action} <FiArrowUpRight className="ml-1 h-3 w-3" />
                        </a>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <motion.div 
                className="overflow-hidden rounded-2xl border border-border/20 shadow-xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="h-64 bg-muted/30 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <i className="fas fa-map-marker-alt text-primary"></i>
                    </div>
                    <h4 className="font-semibold text-foreground mb-2">Our Location</h4>
                    <p className="text-sm text-muted-foreground mb-4">Almawarid Building, Industrial Area 3, Sharjah, UAE</p>
                    <a 
                      href="https://maps.app.goo.gl/DgTfJ51jwL9Ma5wV7" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                    >
                      View on map <i className="fas fa-external-link-alt ml-1 text-xs"></i>
                    </a>
                  </div>
                </div>
                <div className="p-6 bg-card/50 backdrop-blur-sm border-t border-border/20">
                  <h4 className="font-semibold text-foreground mb-3">Get Directions</h4>
                  <a 
                    href="https://maps.app.goo.gl/DgTfJ51jwL9Ma5wV7" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <span className="text-sm font-medium">Open in Google Maps</span>
                    <i className="fas fa-external-link-alt text-xs text-muted-foreground"></i>
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-100px" }}
              className="lg:sticky lg:top-8"
            >
              <div className="lg:pr-4">
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
